import React, { useMemo, useState } from "react";
import { ChevronRight, ChevronDown, UserX } from "lucide-react";

const KIND_STYLE = {
  executive_office: { label: "EXEC OFFICE", color: "#D4AF37" },
  division: { label: "DIVISION", color: "#7DD3FC" },
  department: { label: "DEPARTMENT", color: "#C0C7DA" },
  corporate_support: { label: "CORPORATE SUPPORT", color: "#F0A6A6" },
  ring_fence: { label: "RING-FENCED", color: "#EAB308" },
};

/**
 * Iterative flattener: walks the department tree DFS starting from roots and
 * emits a flat array of {dept, depth} — respecting the `openIds` set to skip
 * children of collapsed nodes. This dodges the Babel-plugin stack overflow
 * caused by a self-recursive JSX component.
 */
function flattenTree(roots, childrenMap, openIds) {
  const out = [];
  const stack = roots.map((d) => ({ dept: d, depth: 0 })).reverse();
  while (stack.length) {
    const node = stack.pop();
    out.push(node);
    if (openIds.has(node.dept.id)) {
      const kids = (childrenMap[node.dept.id] || []).slice().reverse();
      for (const k of kids) stack.push({ dept: k, depth: node.depth + 1 });
    }
  }
  return out;
}

function PositionRow({ pos }) {
  return (
    <div className="flex items-center gap-3 p-2.5 bg-[#0A111F]/60 border border-white/5" data-testid={"pos-" + pos.id}>
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: pos.is_vacant ? "#F59E0B" : "#22C55E" }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{pos.title}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
          <span className="uppercase font-mono">{pos.level}</span>
          {pos.holders && pos.holders.length > 1 && <span>· co-held by {pos.holders.length}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        {pos.is_vacant ? (
          <span className="font-mono text-[10px] tracking-widest text-amber-400 flex items-center gap-1">
            <UserX className="w-3 h-3" /> VACANT
          </span>
        ) : (
          <div className="flex items-center gap-2">
            {(pos.holders || []).slice(0, 3).map((h) => (
              <span key={h.id} className="text-xs text-slate-300" title={h.email}>{h.name}</span>
            ))}
            {pos.holders && pos.holders.length > 3 && <span className="text-xs text-slate-500">+{pos.holders.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function DeptRow({ dept, depth, hasContent, isOpen, positions, onToggle }) {
  const kind = KIND_STYLE[dept.kind] || KIND_STYLE.department;
  const filled = positions.filter((p) => !p.is_vacant).length;
  return (
    <div style={{ marginLeft: depth * 20 }} data-testid={"dept-" + dept.id}>
      <div
        className="nexus-card p-3 flex items-center gap-3 cursor-pointer hover:border-[#D4AF37]/30"
        onClick={() => hasContent && onToggle(dept.id)}
      >
        {hasContent ? (
          isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
        ) : <div className="w-3.5" />}
        <div className="w-1 h-8 rounded" style={{ background: kind.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium truncate">{dept.name}</span>
            {dept.code && <span className="font-mono text-[10px] text-slate-500">{dept.code}</span>}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: kind.color }}>
            {kind.label} {dept.dotted_line_department_ids && dept.dotted_line_department_ids.length > 0 && "· has dotted-line links"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-xs text-white">{positions.length} pos</div>
          <div className="text-[10px] text-slate-500">{filled} filled</div>
        </div>
      </div>
      {isOpen && positions.length > 0 && (
        <div className="ml-6 mt-2 mb-2 space-y-2 border-l border-white/5 pl-4">
          {positions.map((p) => (<PositionRow key={p.id} pos={p} />))}
        </div>
      )}
    </div>
  );
}

export default function OrgTree({ departments, positions }) {
  const roots = useMemo(() => departments.filter((d) => !d.parent_id), [departments]);
  const childrenMap = useMemo(() => {
    const m = {};
    for (const d of departments) {
      if (d.parent_id) {
        m[d.parent_id] = m[d.parent_id] || [];
        m[d.parent_id].push(d);
      }
    }
    return m;
  }, [departments]);
  const positionsByDept = useMemo(() => {
    const m = {};
    for (const p of positions) {
      m[p.department_id] = m[p.department_id] || [];
      m[p.department_id].push(p);
    }
    return m;
  }, [positions]);

  // Default: root + first-level children open
  const [openIds, setOpenIds] = useState(() => {
    const s = new Set(roots.map((r) => r.id));
    for (const r of roots) for (const k of (childrenMap[r.id] || [])) s.add(k.id);
    return s;
  });

  const toggle = (id) => {
    setOpenIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const flat = flattenTree(roots, childrenMap, openIds);

  return (
    <div className="space-y-2">
      {flat.map(({ dept, depth }) => (
        <DeptRow
          key={dept.id}
          dept={dept}
          depth={depth}
          hasContent={(childrenMap[dept.id] || []).length > 0 || (positionsByDept[dept.id] || []).length > 0}
          isOpen={openIds.has(dept.id)}
          positions={positionsByDept[dept.id] || []}
          onToggle={toggle}
        />
      ))}
    </div>
  );
}
